
import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { Card, Input, Row, Col } from "reactstrap";
import DataTable from "react-data-table-component";
import "react-datepicker/dist/react-datepicker.css";
import { useDataContext } from "../../../../context/hooks/useDataContext";
import { baseURL, imageURL } from "../../../../Services/api/baseURL";
import dummyImg from "../../../../../src/assets/images/product/1.png";
import { RefreshCcw } from "lucide-react";

const TopSellingProductCard = () => {
  const {
    dashboardTopProducts,
    setDashboardTopProducts,
    categoryData
  } = useDataContext();

  const [categoryId, setCategoryId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const [allTopProducts, setAllTopProducts] = useState([]);

  const getTopSellingProducts = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem("token"));

      const params = {
        page,
        limit,
      };

      if (categoryId) params.categoryId = categoryId;
      if (startDate && endDate) {
        params.start_date = moment(startDate).format("YYYY-MM-DD");
        params.end_date = moment(endDate).format("YYYY-MM-DD");
      }

      const res = await axios.get(`${baseURL}/api/dashboard/get-all-top-selling`, {
        params,
        headers: { Authorization: `${token}` },
      });

      if (res?.status === 200) {
        const responseData = res.data?.data?.data || [];
        const totalCount = res.data?.data?.total || 0;

   
        setDashboardTopProducts(responseData);
        setAllTopProducts(responseData);
        setTotalRows(totalCount);
      } else {
        setDashboardTopProducts([]);
        setAllTopProducts([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching top-selling products:", error);
      setDashboardTopProducts([]);
      setAllTopProducts([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };


  // For dates, only fetch when BOTH are selected
  useEffect(() => {
    getTopSellingProducts();
  }, [page, limit, categoryId]);

  // Separate effect for date range - only trigger when both dates are selected or both are cleared
  useEffect(() => {
    // Only fetch if both dates are selected OR both are null
    if ((startDate && endDate) || (!startDate && !endDate)) {
      if (page !== 1) {
        setPage(1);
      } else {
        getTopSellingProducts();
      }
      setResetPaginationToggle(!resetPaginationToggle);
    }
  }, [startDate, endDate]);

  // Reset to page 1 when category changes
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
      setResetPaginationToggle(!resetPaginationToggle);
    }
  }, [categoryId]);
  // DataTable columns
  const orderColumns = [
    {
      name: "RANK",
      width: "70px",
      center: true,
      cell: (row, index) => {
        const rank = index + 1 + (page - 1) * limit;
        console.log('Rendering rank:', rank, 'for index:', index); // Debug
        return <p className="mb-0">#{rank}</p>;
      }
    },
     {
      center: true,
      width: "40px",
      cell: (row) => {
        const image = row?.productImage || row?.variants?.variantImage || dummyImg;
        return (
          <Row className="product_data py-1 flex justify-center items-center  mb-0 p-0 border-0">
            <Col lg={2}>
              <div className="product_img dash_product_img h-9 w-8  ">
                <img src={imageURL + image}  alt={"name"}  />
              </div>
            </Col>
          </Row>
        );
      },
    },
    {
      name: "PRODUCT",
      center: true,
      cell: (row) => {
        const name = row?.productName || row?.variants?.variantName || "N/A";
        return (
              <>
              <div className="pl-5 whitespace-nowrap">{name}</div>
              </>
        );
      },
    },
    {
      name: "CATEGORY",
      width: "150px",
      center: true,
      cell: (row) =>
        row?.category?.categoryName ||
        row?.CategoryName ||
        row?.variants?.product?.subCategory?.sub_collection_name ||
        "N/A",
    },
    {
      name: "SUB CATEGORY",
      width: "150px",
      center: true,
      cell: (row) =>
        row?.subCategory?.subCategoryName ||
        row?.subCategoryName ||
        row?.variants?.product?.subCategory?.sub_collection_name ||
        "N/A",
    },
    {
      name: "TOTAL UNITS SOLD",
            width: "150px",
      center: true,
      cell: (row) => (row?.totalQuantitySold !== undefined ? `${row.totalQuantitySold}` : "N/A"),
    },
    {
      name: "TOTAL REVENUE",
            width: "150px",
      center: true,
      cell: (row) => (row?.totalRevenue !== undefined ? `$${row.totalRevenue.toFixed(2)}` : "N/A"),
    },
  ];

  return (
    <Card>
      <div className="card-header" style={{ padding: "15px" }}>
        <h6 className="mb-3 w-100">Top Selling Products</h6>
        <div className="d-flex align-items-center file-content file-content-dash">
          {/* Category Filter */}
          <Input
            type="select"
            name="product_id"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="me-2"
            style={{ width: "400px", padding: "8px 30px 8px 12px" }}
          >
            <option value="">All Categories</option>
            {categoryData &&
              categoryData.length > 0 &&
              categoryData
                .sort((a, b) => a.collection_name.localeCompare(b.collection_name))
                .map((data) => (
                  <option key={data._id} value={data._id}>
                    {data.collection_name}
                  </option>
                ))}
          </Input>

          <div className="flex gap-3">
            <input
              type="date"
              name="start_date"
              value={startDate || ""}
              onChange={(e) => setStartDate(e.target.value)}
              className="border p-1 rounded"
              max={new Date().toISOString().split("T")[0]}
            />
            <input
              type="date"
              name="end_date"
              value={endDate || ""}
              onChange={(e) => setEndDate(e.target.value)}
              className="border p-1 rounded"
              max={new Date().toISOString().split("T")[0]}
            />
            <button
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
              className="flex justify-center items-center text-red-500 gap-2 font-semibold"
            >
              <RefreshCcw size={14} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

  

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div className="spinner-border text-[#d3178a]" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : dashboardTopProducts.length > 0 ? (
        <DataTable
          key={`${page}-${limit}-${resetPaginationToggle}`}
          data={dashboardTopProducts}
          columns={orderColumns}
          striped
          pagination
          // fixedHeader
          center
          paginationServer
          paginationTotalRows={totalRows}
          paginationDefaultPage={page}
          onChangePage={(newPage) => {
            setPage(newPage);
          }}
          onChangeRowsPerPage={(newLimit, newPage) => {
            setLimit(newLimit);
            setPage(1);
            setResetPaginationToggle(!resetPaginationToggle);
          }}
          paginationPerPage={limit}
          paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
          paginationResetDefaultPage={resetPaginationToggle}
        />
      ) : (
        <p className="my-5 text-center">No Data Found</p>
      )}
    </Card>
  );
};

export default TopSellingProductCard;
